/**
 * THE TYPED CLIENT LAYER FOR THE CABINET SURFACE.
 *
 * No screen calls `fetch`. Every screen calls a method on this interface, and every method's
 * argument and return type is DERIVED from `shared/types/generated/authors-api.d.ts` through the
 * helpers in ./generated — never hand-written. An operation missing from `operations` cannot be
 * added here, which is what makes "the operation does not exist" a compile error instead of a
 * runtime 404.
 *
 * WHY AN INTERFACE. Every screen runs against fixtures in the default build and against the real
 * host in a live one; making that swap ONE CHANGE rather than a hundred is the whole reason this
 * file exists - see ./cabinet-api-source.ts, the seam.
 *
 * ERRORS ARE THROWN, NOT RETURNED. Both implementations throw `ApiProblem` for a problem document
 * and `TransportFailure` for a request that never landed; ../state/use-async catches. A screen that
 * wants to branch on a refusal branches on `error_code`, which the contract says is the only thing
 * a client may branch on.
 */
import { createContext, useContext } from 'react';
import type { Cursor, OperationBody, OperationQuery, OperationResult, PublicId } from './generated';
import type {
  CreateVideoDraftRequest,
  SchemaAccountDeletionAck,
  TranslationState,
} from './generated';

export interface CabinetApi
  extends CabinetAccountApi, CabinetReaderApi, CabinetEarlierMonthsApi, CabinetVideoApi {
  /* ---- session -------------------------------------------------------------------------- */

  /**
   * The one read that renders the whole app. `cabinetReachable` is true for any author row: an
   * applicant (their pages empty), an approved author, and a disabled one, who sees the reason
   * and has payout actions refused — the cabinet is never a locked door (authors-api.d.ts).
   */
  getAuthorSession(signal?: AbortSignal): Promise<OperationResult<'getAuthorSession'>>;

  /* ---- dashboard ------------------------------------------------------------------------ */

  /**
   * `from`/`to` are optional ON PURPOSE at every call site in this app. Both are "resolved as a
   * UTC calendar day on the SERVER clock", and omitting them takes "the policy-valued default
   * window". A browser that computed its own default would disagree with every figure it then
   * rendered — a client in UTC+13 and the numbers do not agree about which day was asked for.
   */
  getDashboard(
    query?: OperationQuery<'getDashboard'>,
    signal?: AbortSignal,
  ): Promise<OperationResult<'getDashboard'>>;

  /**
   * Per-article numbers over a range. Nothing is computed for this response: these are the daily
   * cells `getDashboard` already sums, regrouped per article. Keyset-paged.
   */
  listArticleStats(
    query?: OperationQuery<'listArticleStats'>,
    signal?: AbortSignal,
  ): Promise<OperationResult<'listArticleStats'>>;

  /**
   * One article's figures since it went out - views, likes, comments, the tips sent on it - and its
   * path on the website while it is published: the article's own page (ArticleReadView.tsx).
   */
  getArticleSummary(
    articleId: PublicId,
    signal?: AbortSignal,
  ): Promise<OperationResult<'getArticleSummary'>>;

  /* ---- articles ------------------------------------------------------------------------- */

  listOwnArticles(
    query?: OperationQuery<'listOwnArticles'>,
    signal?: AbortSignal,
  ): Promise<OperationResult<'listOwnArticles'>>;

  /** 201 with a `Location`. The body is the new, empty working copy. */
  createDraft(): Promise<OperationResult<'createDraft'>>;

  /** Only from `published`. 409 when it is not. */
  archiveOwnArticle(articleId: PublicId): Promise<OperationResult<'archiveOwnArticle'>>;

  /** Only what THIS AUTHOR archived — staff's archive answers 403 ARCHIVE_ACTOR_MISMATCH. */
  unarchiveOwnArticle(articleId: PublicId): Promise<OperationResult<'unarchiveOwnArticle'>>;

  /** HARD DELETE, and only of something never published. 204, no body. */
  deleteNeverPublishedDraft(articleId: PublicId): Promise<void>;

  /** Only from `in_review`. */
  withdrawArticle(articleId: PublicId): Promise<OperationResult<'withdrawArticle'>>;

  /** Only from `scheduled`, and only before go-live. */
  pullScheduledArticle(articleId: PublicId): Promise<OperationResult<'pullScheduledArticle'>>;

  /* ---- comments ------------------------------------------------------------------------- */

  /**
   * Comments readers left on this author's articles, newest first. READ AND ANSWER ONLY: there is
   * no author-side hide and no reader report on this surface (LOCKED D44 clause 1), and hidden
   * comments are ABSENT here exactly as they are from the public thread, so this list cannot be
   * used to discover what was hidden.
   */
  listCommentsOnMyArticles(
    query?: OperationQuery<'listCommentsOnMyArticles'>,
    signal?: AbortSignal,
  ): Promise<OperationResult<'listCommentsOnMyArticles'>>;

  /** Writes an ordinary reader-kind comment at depth 1 carrying this author's own subject. */
  replyToComment(
    commentId: PublicId,
    body: OperationBody<'replyToComment'>,
  ): Promise<OperationResult<'replyToComment'>>;

  /* ---- payments ------------------------------------------------------------------------- */

  /**
   * A READ-TIME SUM OVER THE LEDGER, NEVER A STORED BALANCE. `availableMinor` may be negative and
   * is never clamped, and `currentMonth` — the running mid-month figure — arrives on THIS response
   * rather than from a second operation.
   */
  getPaymentsView(signal?: AbortSignal): Promise<OperationResult<'getPaymentsView'>>;

  listStatements(
    query?: OperationQuery<'listStatements'>,
    signal?: AbortSignal,
  ): Promise<OperationResult<'listStatements'>>;

  /** `period` is `YYYY-MM`, UTC, because months are UTC everywhere money is measured. */
  getStatement(period: string, signal?: AbortSignal): Promise<OperationResult<'getStatement'>>;

  /**
   * THE SAME ADDRESS AS `getStatement`, asked for as `text/csv`. The contract serves both
   * representations by content negotiation and declares NO PDF, despite the prototype's
   * "Statement (PDF)" link.
   */
  getStatementCsv(period: string, signal?: AbortSignal): Promise<string>;

  /** `isSet: false` means the record is EMPTY, NOT ABSENT. Design 25's shape (`PaymentDetails`). */
  getPayoutMethod(signal?: AbortSignal): Promise<PaymentDetails>;
}

/** A page from any keyset-paged read on this surface, narrowed to what a pager needs. */
export interface KeysetPage<TItem> {
  readonly items?: readonly TItem[] | undefined;
  readonly next_cursor?: Cursor | null | undefined;
}

const CabinetApiContext = createContext<CabinetApi | null>(null);

export const CabinetApiProvider = CabinetApiContext.Provider;

/**
 * ★★ IT DOES NOT THROW WHEN NOTHING IS PROVIDED, AND UNTIL 2026-09-22 IT DID - WHICH MADE EVERY
 * CABINET SCREEN A WHITE PAGE IN THE BUILT APP.
 *
 * `web/authors/src/router.tsx` (the `cabinetRoutes` map, `:191-197`) mounts
 * `CABINET_ROUTES[].Component` with NO props and no CabinetApi provider, and `<CabinetProvider>` is
 * exported but used NOWHERE in the app - grep it: only its own definition and the barrel. So every
 * screen calling this hook hit the error boundary. Measured by loading `/articles` from the built
 * bundle: "Something went wrong!", console "No CabinetApi in context".
 *
 * ★ THE SIBLING SEAM HAD ALREADY WRITTEN DOWN THIS EXACT FAILURE AND DEFENDED AGAINST IT.
 * `features/calendar/api/calendar-api.ts` says a screen "that required a provider would be a
 * white screen the moment it was added to that list", and installs a default. This is that fix,
 * one feature over, in the same shape: the default is built by ./cabinet-api-source, which is
 * fixtures until a build sets `VITE_AUTHORS_API_LIVE`. `<CabinetApiProvider>` still overrides it,
 * which is what a test uses.
 */
export function useCabinetApi(): CabinetApi {
  const provided = useContext(CabinetApiContext);
  return provided ?? defaultCabinetApi();
}

/** Assigned by ./cabinet-api-source at module load. Held here so this file has no import cycle
 *  with the implementations it types. */
let fallback: (() => CabinetApi) | undefined;

export function setDefaultCabinetApi(factory: () => CabinetApi): void {
  fallback = factory;
}

function defaultCabinetApi(): CabinetApi {
  if (fallback === undefined) {
    throw new Error(
      'No CabinetApi. Import features/cabinet/api (the barrel), which installs the default ' +
        'implementation, or wrap the screen in <CabinetApiProvider value={…}>.',
    );
  }
  return fallback();
}

/**
 * `requestAccountDeletion`'s 202 body. `OperationResult` reads 200 and then 201 and gives `void`
 * for anything else (./generated.ts), so the schema is reached by its name there - with the other
 * 202, `TranslationState` - rather than being written out by hand.
 */
export type AccountDeletionAck = SchemaAccountDeletionAck;

/**
 * THE ACCOUNT'S OWN SCREENS (register R-005, R-016, R-015): where money goes, birth data and the two
 * public consents, closing the account, and the notification feed with its switches. `CabinetApi`
 * extends this, so a screen still asks for one client. It is declared down here rather than inside
 * `CabinetApi` because the lines above are cited by number from other files.
 */
export interface CabinetAccountApi {
  /* ---- where money goes ----------------------------------------------------------------- */

  /**
   * RE-AUTH GATED, the one such operation on the API: 403 `REAUTH_REQUIRED` when the token's
   * sign-in is older than the server's window (`src/modules/authors/services/reauth.gate.ts`). This
   * client only throws it; the screen signs the author in again and retries ONCE
   * (`routes/payments/PayoutMethodRoute.tsx`). Each save is a new version of the payment details
   * (design 25, P1), and the answer is the whole record as `getPayoutMethod` reads it.
   */
  updatePayoutMethod(body: PaymentDetailsWrite): Promise<PaymentDetails>;

  /**
   * Every agreement document with a version in force or one this author accepted, in the
   * documents' own order (design 25, 3.1): what each blocks, the live version, the one accepted
   * and when, and whether it is owed. Payment details reads the payout documents; Settings all.
   */
  listAgreements(signal?: AbortSignal): Promise<AgreementList>;

  /**
   * One to four documents' live versions accepted in ONE transaction. A version that is unknown or
   * no longer in force is a 404 and nothing is recorded; one already accepted is a no-op, not a
   * 409. The answer is `listAgreements`' body.
   */
  acceptAgreements(body: AgreementAcceptRequest): Promise<AgreementList>;

  /**
   * The countries a payment details record may name (the live ISO 3166-1 rows, `label` the ISO
   * short name). The cabinet names each in American English itself (`Intl.DisplayNames`).
   */
  listCountries(signal?: AbortSignal): Promise<CountryList>;

  /* ---- the profile's private half: signs and consents ----------------------------------- */

  /** The author's own view. `/settings` reads `signs` and `earningsVisibility` off it, nothing else. */
  getOwnProfile(signal?: AbortSignal): Promise<OperationResult<'getOwnProfile'>>;

  /** WRITE-ONLY: no response anywhere returns the date, time or zone. The answer is the signs. */
  setBirthData(body: OperationBody<'setBirthData'>): Promise<OperationResult<'setBirthData'>>;

  /**
   * The settlement typeahead for the birth place (wave 26). Each suggestion is the three
   * `setBirthData` members a pick fills - the id the platform turns into coordinates, the label, and
   * the place's zone as a suggestion. Zero matches is an empty list; 503 when the place search is not
   * answering, which the form says and then lets the birth data be saved without a place.
   */
  searchBirthPlaces(
    query: OperationQuery<'searchBirthPlaces'>,
    signal?: AbortSignal,
  ): Promise<OperationResult<'searchBirthPlaces'>>;

  /** Erases the stored values and the signs and turns the public toggle off. 204, no body. */
  clearBirthData(): Promise<void>;

  /** 422 `BIRTH_DATE_REQUIRED` when turned on with no birth date stored. */
  setComputedSignsVisibility(
    body: OperationBody<'setComputedSignsVisibility'>,
  ): Promise<OperationResult<'setComputedSignsVisibility'>>;

  /** D15's consent: private by default, withdrawable at any time, recorded as an event. */
  setEarningsVisibility(
    body: OperationBody<'setEarningsVisibility'>,
  ): Promise<OperationResult<'setEarningsVisibility'>>;

  /**
   * 202: accepted, and carried out later by the erasure drain, not in this request. 409 when a
   * request is already waiting - there is no read that says so, which is why the screen keeps the
   * answer on screen instead of offering the button again.
   */
  requestAccountDeletion(
    body: OperationBody<'requestAccountDeletion'>,
  ): Promise<AccountDeletionAck>;

  /* ---- notifications -------------------------------------------------------------------- */

  /** Newest first, keyset-paged, and with NO `total` by rule - so no count is drawn anywhere. */
  listNotifications(
    query?: OperationQuery<'listNotifications'>,
    signal?: AbortSignal,
  ): Promise<OperationResult<'listNotifications'>>;

  /** Read up to an instant the client already holds - a row's own `createdAt`, never a clock. */
  markNotificationsRead(
    body: OperationBody<'markNotificationsRead'>,
  ): Promise<OperationResult<'markNotificationsRead'>>;

  getNotificationPreferences(
    signal?: AbortSignal,
  ): Promise<OperationResult<'getNotificationPreferences'>>;

  /** 422 `ALWAYS_ON_CATEGORY` for an always-on category: the screen never offers one. */
  updateNotificationPreferences(
    body: OperationBody<'updateNotificationPreferences'>,
  ): Promise<OperationResult<'updateNotificationPreferences'>>;

  /* ---- the author application (design 25) ----------------------------------------------- */

  /**
   * `GET /v1/application`: where the application stands, and its checklist. The Overview reads
   * `state` for an applicant's "Getting started" - whether the author profile was sent for review -
   * because `GET /v1/me` sends no `applicationState`. The form itself has its own client
   * (features/application).
   */
  getApplication(signal?: AbortSignal): Promise<OperationResult<'getApplication'>>;
}

/**
 * WHAT READERS SEND THE AUTHOR (register R-006, `/messages`): the six `supporter-messages`
 * operations, and the monthly audience read (register R-020, the Statistics screen). Declared down
 * here for the reason `CabinetAccountApi` is: the lines above are cited by number from other files.
 *
 * ★ THE LOCKED MINIMUM IS "READ + REPLY", AND IT MUST NOT BE LOST (D44, decision-log row 53):
 * hide, unhide, retract and translate are the rest of what the contract gives an author, and
 * nothing here lets an author make a message public or act on the reader's side - no such
 * operation exists.
 */
export interface CabinetReaderApi {
  /* ---- supporter messages --------------------------------------------------------------- */

  /**
   * Newest first, keyset-paged, public and private together unless `visibility` narrows it - or
   * `unansweredOnly`, to what waits for a reply (B2-INBOX), which `total` then counts. No sender
   * identity is carried, so nothing on screen can name or count a reader.
   */
  listSupporterMessages(
    query?: OperationQuery<'listSupporterMessages'>,
    signal?: AbortSignal,
  ): Promise<OperationResult<'listSupporterMessages'>>;

  /**
   * 201, the reply. ONE PER MESSAGE, EVER, and never edited: a second is 409
   * `REPLY_ALREADY_EXISTS`, a message its sender took back is an uncoded 409, and a disabled author
   * is refused 403 `AUTHOR_DISABLED` (a reply is public content). Rate-limited: 429.
   */
  replyToSupporterMessage(
    messageId: PublicId,
    body: OperationBody<'replyToSupporterMessage'>,
  ): Promise<OperationResult<'replyToSupporterMessage'>>;

  /** An append-only event, never a delete. 200, the whole message; 409 when nothing is left. */
  retractOwnReply(messageId: PublicId): Promise<OperationResult<'retractOwnReply'>>;

  /** The author's own hide flag only - an editor's hide is a separate flag this never touches. */
  hideSupporterMessage(messageId: PublicId): Promise<OperationResult<'hideSupporterMessage'>>;

  /** Lifts the author's flag only; a message an editor hid stays hidden. */
  unhideSupporterMessage(messageId: PublicId): Promise<OperationResult<'unhideSupporterMessage'>>;

  /**
   * 202, A JOB AND NOT A TRANSLATION: `queued`, or `produced` when one exists already. The text is
   * read back from the message on the next list read - there is no poll route.
   */
  requestSupporterMessageTranslation(
    messageId: PublicId,
    body: OperationBody<'requestSupporterMessageTranslation'>,
  ): Promise<TranslationState>;

  /* ---- the monthly audience ------------------------------------------------------------- */

  /**
   * One SEALED month on one dimension. 404 `MONTH_NOT_SEALED` for a month not frozen yet - the open
   * month always. The dimension's total is absent ON PURPOSE (a subtraction would reveal the merged
   * small groups), so no screen may add one up.
   */
  getAudienceBreakdown(
    query: OperationQuery<'getAudienceBreakdown'>,
    signal?: AbortSignal,
  ): Promise<OperationResult<'getAudienceBreakdown'>>;

  /* ---- the Statistics chart (design 18) ------------------------------------------------- */

  /**
   * Every day of a range with its figures, oldest first, `0` where nothing was counted - the
   * author's, or one of their articles' with `articleId`. Same defaults as `getDashboard`; at most
   * 366 days. `readers` is that day's alone and is never added up across days. A server from
   * before this read answers 404 (with or without a problem body), and a screen then draws no chart.
   */
  getDashboardDays(
    query?: OperationQuery<'getDashboardDays'>,
    signal?: AbortSignal,
  ): Promise<OperationResult<'getDashboardDays'>>;
}

/**
 * THE AUTHOR'S EARLIER MONTHS (design 17, the owner, 2026-10-05): the months before payouts were
 * made here, as their monthly reports stated them - for the author's records on /payments. Declared
 * down here for the reason `CabinetAccountApi` is: the lines above are cited by number from other
 * files.
 *
 * ★ HISTORY TO LOOK AT, NEVER MONEY. None of it is in the balance: what was still owed when these
 * figures began is `getPaymentsView`'s `breakdown.openingBalanceMinor` ("From earlier"), so adding
 * these months to anything would count the same money twice. Nothing reads them into a sum.
 */
export interface CabinetEarlierMonthsApi {
  /**
   * Every earlier month on record for the signed-in author, newest first, not paged (eight at
   * most); an empty list when there is none. 401 / 403 `AUTHOR_DISABLED` as `getPaymentsView`. A
   * figure the report did not state readably is null, never a zero, and `payoutStatus` is what was
   * found about the month - `paid`, `carried` to the next month, or `unknown`.
   */
  listEarlierMonths(signal?: AbortSignal): Promise<OperationResult<'listEarlierMonths'>>;
}

/**
 * VIDEO (v1.0.29): staff let some authors publish videos, and an author without it may ask. A video
 * is a kind of article; the list reads videos with `listOwnArticles({ kind: 'video' })`.
 */
export interface CabinetVideoApi {
  /**
   * 201, the new empty video draft. 403 `VIDEO_ACCESS_REQUIRED` unless staff granted access. With
   * `{ stream: true }` (1.2.0) a live stream draft instead, 403 `STREAM_ACCESS_REQUIRED` without
   * stream access; a draft is a stream or a video for good.
   */
  createVideoDraft(body?: CreateVideoDraftRequest): Promise<OperationResult<'createVideoDraft'>>;

  /** Answers the session, whose `videoAccess` now reads `requested` (or still `granted`). */
  requestVideoAccess(): Promise<OperationResult<'requestVideoAccess'>>;

  /** The same ask for live streams (1.2.0): the session, `streamAccess` now `requested`. */
  requestStreamAccess(): Promise<OperationResult<'requestStreamAccess'>>;

  /** The author's videos with all-time views (one reader a day), likes and comments. */
  listVideoStats(signal?: AbortSignal): Promise<OperationResult<'listVideoStats'>>;
}

/* ---------------------------------------------------------------------------------------------
 * ★ DESIGN 25'S SHAPES (T22, T24; design 25 sections 3.1, 3.2 and 5), derived from the generated
 * operations like every other shape here. `PaymentDetails` is the grown `PayoutMethod`,
 * `PaymentDetailsWrite` the grown `PayoutMethodWrite`; the names stay because the screens say them.
 * ------------------------------------------------------------------------------------------- */

/**
 * `GET /v1/payout-method` as design 25 serves it: the record plus the legal name, the address in
 * full, the confirmation and what completeness needs (P5). `missing` holds the parts of
 * `PAYMENT_DETAILS_PARTS`; it is empty, and `complete` true, only when nothing is missing.
 */
export type PaymentDetails = OperationResult<'getPayoutMethod'>;

/** `PUT /v1/payout-method`'s body, the legal name and "these details are true" (P13) in it. */
export type PaymentDetailsWrite = OperationBody<'updatePayoutMethod'>;

/** The parts `PaymentDetails.missing` names - the contract's own list. */
export type PaymentDetailsPart = NonNullable<PaymentDetails['missing']>[number];

/** The same parts as a value, in the order the page says them. */
export const PAYMENT_DETAILS_PARTS = [
  'legal_name',
  'country',
  'paypal_email',
  'details_true',
  'payout_terms',
  'payment_data_notice',
] as const satisfies readonly PaymentDetailsPart[];

/** The agreement documents design 25 knows (P9). The list is served; these name the four. */
export const AGREEMENT_DOCUMENT = {
  authorAgreement: 'creator_program',
  editorialStandards: 'editorial_standards',
  payoutTerms: 'payout_terms',
  paymentDataNotice: 'payment_data_notice',
} as const;

/** `listAgreements`, and the answer of `acceptAgreements` (its 201). */
export type AgreementList = OperationResult<'listAgreements'>;

/**
 * One document, as `listAgreements` lists it. ★ `live` IS NULL for a document this author accepted
 * that has no version in force now: there is nothing to tick and nothing in force to link to.
 */
export type Agreement = AgreementList['items'][number];

/** What a document holds back until it is accepted: sending for review, or a complete record. */
export type AgreementBlocks = Agreement['blocks'];

/** `acceptAgreements`' body: one to four documents, each named once, with the version shown. */
export type AgreementAcceptRequest = OperationBody<'acceptAgreements'>;

/** `listCountries`: the live ISO 3166-1 codes, each with the ISO short name. */
export type CountryList = OperationResult<'listCountries'>;

/** A payout's block reason (`PaymentsView.payouts[].blockReason`), design 25's among them. */
export type PayoutBlockReason = NonNullable<
  NonNullable<OperationResult<'getPaymentsView'>['payouts']>[number]['blockReason']
>;
